<script lang="ts">
  import type { Segment } from '$lib/stores/aiFeatures'
  import { settingsStore } from '$lib/stores/settings'

  /**
   * PanelSynchronizerController: Handles panel synchronization
   * Single Responsibility: Synchronize height and scroll between panels
   */

  interface Props {
    segments: Segment[]
    hoveredSegmentId: string | null
    onSegmentHover?: (segmentId: string | null) => void
  }

  let { 
    segments,
    hoveredSegmentId = $bindable(null),
    onSegmentHover
  }: Props = $props()

  // Synchronize segment heights between panels
  $effect(() => {
    if (!segments.length) return
    
    const syncHeights = () => {
      requestAnimationFrame(() => {
        // Get computed font size for dynamic calculations
        const rootStyle = getComputedStyle(document.documentElement)
        const editorFontSize = rootStyle.getPropertyValue('--editor-font-size')
        const fontSize = editorFontSize ? parseFloat(editorFontSize) : 14
        
        segments.forEach((_, index) => {
          const leftRow = document.querySelector(`.panel.original .segment-row[data-index="${index}"]`) as HTMLElement
          const rightRow = document.querySelector(`.panel.suggested .segment-row[data-index="${index}"]`) as HTMLElement
          
          if (leftRow && rightRow) {
            // Reset heights first on both row and box
            leftRow.style.height = ''
            rightRow.style.height = ''
            
            const leftBox = leftRow.querySelector('.segment-box') as HTMLElement
            const rightBox = rightRow.querySelector('.segment-box') as HTMLElement
            
            if (leftBox && rightBox) {
              // Reset box heights
              leftBox.style.height = ''
              rightBox.style.height = ''
              
              // Get content heights
              const leftContent = leftBox.querySelector('.segment-content') as HTMLElement
              const rightContent = rightBox.querySelector('.segment-content') as HTMLElement
              
              if (leftContent && rightContent) {
                // Force layout recalculation
                leftContent.style.height = 'auto'
                rightContent.style.height = 'auto'
                
                const leftContentHeight = leftContent.scrollHeight
                const rightContentHeight = rightContent.scrollHeight
                
                // Calculate padding in em converted to px
                // segment-box has padding: 0.6em 0.5em 0.6em 1.8em
                const paddingEm = 1.2 // top + bottom = 0.6em + 0.6em
                const paddingPx = paddingEm * fontSize
                
                // Add extra buffer for small font sizes to prevent cutoff
                // Small fonts (12px) need more relative space for line-height
                const extraBuffer = fontSize <= 12 ? 4 : 2
                
                // segment-box has margin-bottom: 0.5em
                const marginEm = 0.5
                const marginPx = marginEm * fontSize
                
                // Calculate total height needed with extra buffer
                const leftTotalHeight = leftContentHeight + paddingPx + extraBuffer
                const rightTotalHeight = rightContentHeight + paddingPx + extraBuffer
                
                // Minimum height: 4em as defined in CSS
                const minHeightPx = 4 * fontSize
                
                const maxHeight = Math.max(leftTotalHeight, rightTotalHeight, minHeightPx)
                
                // Apply same height to both boxes
                leftBox.style.height = `${maxHeight}px`
                rightBox.style.height = `${maxHeight}px`
                
                // Also set row height to ensure proper containment
                // Row padding: 0.8em top + 0.8em bottom = 1.6em
                const rowPaddingPx = 1.6 * fontSize
                const rowHeight = maxHeight + rowPaddingPx
                
                leftRow.style.minHeight = `${rowHeight}px`
                rightRow.style.minHeight = `${rowHeight}px`
              }
            }
          }
        })
      })
    }
    
    // Initial sync with small delay for content to render
    setTimeout(syncHeights, 100)
    
    // Subscribe to settings changes for font size updates
    const unsubscribe = settingsStore.subscribe(() => {
      // Re-sync heights when font size changes
      setTimeout(syncHeights, 50)
    })
    
    // Re-sync on window resize and mutation
    const resizeHandler = () => syncHeights()
    window.addEventListener('resize', resizeHandler)
    
    // Watch for container size changes
    const container = document.querySelector('.split-editor')
    if (container) {
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(syncHeights, 50)
      })
      resizeObserver.observe(container)
      
      return () => {
        window.removeEventListener('resize', resizeHandler)
        resizeObserver.disconnect()
        unsubscribe()
      }
    }
    
    return () => {
      window.removeEventListener('resize', resizeHandler)
      unsubscribe()
    }
  })

  // Synchronize scroll between panels (only right panel scrolls)
  $effect(() => {
    const leftPanel = document.querySelector('.panel.original .panel-content')
    const rightPanel = document.querySelector('.panel.suggested .panel-content')
    
    if (!leftPanel || !rightPanel) return
    
    // Left panel follows right panel scroll
    const handleRightScroll = () => {
      // Calculate scroll percentage
      const scrollPercentage = rightPanel.scrollTop / (rightPanel.scrollHeight - rightPanel.clientHeight)
      
      // Apply same percentage to left panel
      leftPanel.scrollTop = scrollPercentage * (leftPanel.scrollHeight - leftPanel.clientHeight)
    }
    
    // Enable wheel scrolling on left panel but apply to right
    const handleLeftWheel = (e) => {
      e.preventDefault()
      rightPanel.scrollTop += e.deltaY
    }
    
    rightPanel.addEventListener('scroll', handleRightScroll)
    leftPanel.addEventListener('wheel', handleLeftWheel, { passive: false })
    
    return () => {
      rightPanel.removeEventListener('scroll', handleRightScroll)
      leftPanel.removeEventListener('wheel', handleLeftWheel)
    }
  })

  // Handle hover events from ErrorDisplay cards
  $effect(() => {
    if (!segments) return
    
    const handleCardHover = (e: CustomEvent) => {
      const segmentId = e.detail?.segmentId
      if (!segmentId) return
      
      const index = segments.findIndex(s => s.id === segmentId)
      if (index === -1) return
      
      const segmentRow = document.querySelector(
        `.panel.suggested .segment-row[data-index="${index}"]`
      ) as HTMLElement
      
      if (segmentRow) {
        const panel = document.querySelector('.panel.suggested .panel-content') as HTMLElement
        if (panel) {
          const panelRect = panel.getBoundingClientRect()
          const rowRect = segmentRow.getBoundingClientRect()
          const relativeTop = segmentRow.offsetTop
          
          // Smooth scroll to center the segment in view
          const targetScroll = relativeTop - (panelRect.height / 2) + (rowRect.height / 2)
          
          panel.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth'
          })
          
          // Add hover effect
          segmentRow.classList.add('hovered-from-card')
          hoveredSegmentId = segmentId
        }
      }
    }
    
    const handleCardHoverEnd = () => {
      document.querySelectorAll('.hovered-from-card').forEach(el => {
        el.classList.remove('hovered-from-card')
      })
      hoveredSegmentId = null
    }
    
    window.addEventListener('ai-card-hover', handleCardHover as EventListener)
    window.addEventListener('ai-card-hover-end', handleCardHoverEnd)
    
    return () => {
      window.removeEventListener('ai-card-hover', handleCardHover as EventListener)
      window.removeEventListener('ai-card-hover-end', handleCardHoverEnd)
    }
  })
</script>